import React, { useState } from 'react';
import { Upload, FileSpreadsheet, X, Download, FileDown, Trash2, BarChart3, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BalanceReview() {
  const [projects, setProjects] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setLoading(true);
    setUploadError('');

    try {
      const newProjects = [];

      for (const file of files) {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          throw new Error(`الملف ${file.name} فارغ`);
        }

        const accounts = jsonData.map((row, index) => {
          const accountNumber = row['رقم الحساب'] || row['account_number'] || row['رقم'] || '';
          const accountName = row['اسم الحساب'] || row['account_name'] || row['الحساب'] || row['اسم'] || '';
          const balance = parseFloat(row['الرصيد'] || row['balance'] || row['Balance'] || 0);

          return {
            id: `${file.name}-${index}`,
            accountNumber: String(accountNumber),
            accountName: String(accountName),
            balance: balance
          };
        }).filter(acc => acc.accountName);

        newProjects.push({
          id: Date.now() + Math.random(),
          name: file.name.replace('.xlsx', '').replace('.xls', ''),
          fileName: file.name,
          accounts: accounts,
          uploadDate: new Date().toISOString()
        });
      }

      setProjects([...projects, ...newProjects]);
      event.target.value = '';
    } catch (error) {
      setUploadError(`خطأ في معالجة الملفات: ${error.message}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (projects.length < 2) {
      alert('الرجاء رفع مشروعين على الأقل للمقارنة');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/balance/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projects })
      });

      const data = await response.json();
      setComparisonData(data);
    } catch (error) {
      console.error('خطأ في المقارنة:', error);
      alert('حدث خطأ أثناء المقارنة');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!comparisonData) return;

    try {
      const response = await fetch(`${API}/balance/export-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comparisonData })
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `مقارنة_ميزان_المراجعة_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
    } catch (error) {
      console.error('خطأ في التصدير:', error);
      alert('حدث خطأ أثناء التصدير');
    }
  };

  const handleExportPDF = async () => {
    if (!comparisonData) return;

    try {
      const response = await fetch(`${API}/balance/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comparisonData })
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `مقارنة_ميزان_المراجعة_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
    } catch (error) {
      console.error('خطأ في التصدير:', error);
      alert('حدث خطأ أثناء التصدير');
    }
  };

  const deleteProject = (projectId) => {
    setProjects(projects.filter(p => p.id !== projectId));
    setComparisonData(null);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('ar-SA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="text-center mb-5 sm:mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-3 shadow-lg">
          <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1.5">
          ميزان مراجعة الحسابات
        </h1>
        <p className="text-sm sm:text-base text-gray-500">
          قارن موازين المراجعة لعدة مشاريع بسهولة
        </p>
      </div>

      {/* Upload Section */}
      <Card className="mb-4 sm:mb-5 shadow-md border-0 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-3 sm:p-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Upload className="w-5 h-5" />
            رفع ملفات ميزان المراجعة
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-5">
          <label className="block cursor-pointer mb-4">
            <div className="border-2 border-dashed border-indigo-200 rounded-xl p-5 sm:p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition-all">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 bg-indigo-100 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-500" />
              </div>
              <p className="text-sm sm:text-base font-semibold text-gray-700 mb-1">
                اضغط لرفع ملفات Excel
              </p>
              <p className="text-xs sm:text-sm text-gray-500">
                يمكنك رفع عدة ملفات (.xlsx, .xls)
              </p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-2">
                التنسيق: رقم الحساب | اسم الحساب | الرصيد
              </p>
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              disabled={loading}
            />
          </label>

          {/* Download Templates */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
            <a
              href="/نموذج_ميزان_المراجعة_فارغ.xlsx"
              download
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
            >
              <Download className="w-4 h-4" />
              نموذج فارغ
            </a>
            <a
              href="/نموذج_ميزان_المراجعة_بأمثلة.xlsx"
              download
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
            >
              <Download className="w-4 h-4" />
              نموذج بأمثلة
            </a>
          </div>

          {uploadError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {uploadError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Uploaded Projects */}
      {projects.length > 0 && (
        <Card className="mb-4 sm:mb-5 shadow-md border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-3 sm:p-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FileSpreadsheet className="w-5 h-5" />
              المشاريع المرفوعة ({projects.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 text-sm sm:text-base truncate mb-1">
                        {project.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {project.accounts.length} حساب
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteProject(project.id)}
                      className="text-red-500 hover:bg-red-50 h-8 w-8 p-0 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-center">
              <Button
                onClick={handleCompare}
                disabled={loading || projects.length < 2}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-bold rounded-xl shadow-lg disabled:opacity-50"
              >
                <BarChart3 className="w-5 h-5 ml-2" />
                {loading ? 'جاري المقارنة...' : 'مقارنة الموازين'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison Results */}
      {comparisonData && (
        <Card className="shadow-md border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <BarChart3 className="w-5 h-5" />
                نتائج المقارنة
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={handleExportExcel}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm px-3 py-2 h-auto"
                  size="sm"
                >
                  <Download className="w-3.5 h-3.5 ml-1.5" />
                  Excel
                </Button>
                <Button
                  onClick={handleExportPDF}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm px-3 py-2 h-auto"
                  size="sm"
                >
                  <FileDown className="w-3.5 h-3.5 ml-1.5" />
                  PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-5">
            {/* Summary Statistics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
              {[
                { label: 'إجمالي الحسابات', value: comparisonData.totalAccounts, color: 'blue' },
                { label: 'الحسابات المشتركة', value: comparisonData.commonAccounts, color: 'emerald' },
                { label: 'عدد المشاريع', value: comparisonData.projectCount, color: 'purple' },
                { label: 'حسابات فريدة', value: comparisonData.uniqueAccounts, color: 'amber' },
              ].map((stat, idx) => (
                <div key={idx} className={`bg-${stat.color}-50 p-3 sm:p-4 rounded-xl border border-${stat.color}-100`}>
                  <p className="text-[10px] sm:text-xs text-gray-600 mb-1">{stat.label}</p>
                  <p className={`text-lg sm:text-2xl font-bold text-${stat.color}-600`}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
              <div className="min-w-[600px]">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 p-2 sm:p-3 text-right font-bold text-xs sm:text-sm">
                        رقم الحساب
                      </th>
                      <th className="border border-gray-200 p-2 sm:p-3 text-right font-bold text-xs sm:text-sm">
                        اسم الحساب
                      </th>
                      {comparisonData.projectNames.map((name, idx) => (
                        <th
                          key={idx}
                          className="border border-gray-200 p-2 sm:p-3 text-center font-bold bg-indigo-50 text-xs sm:text-sm"
                        >
                          {name}
                        </th>
                      ))}
                      <th className="border border-gray-200 p-2 sm:p-3 text-center font-bold bg-amber-50 text-xs sm:text-sm">
                        الفرق
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.accounts.map((account, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="border border-gray-200 p-2 sm:p-3 text-right font-mono text-xs sm:text-sm">
                          {account.accountNumber}
                        </td>
                        <td className="border border-gray-200 p-2 sm:p-3 text-right font-semibold text-xs sm:text-sm">
                          {account.accountName}
                        </td>
                        {account.balances.map((balance, bIdx) => (
                          <td
                            key={bIdx}
                            className={`border border-gray-200 p-2 sm:p-3 text-center font-mono text-xs sm:text-sm ${
                              balance === null
                                ? 'bg-gray-100 text-gray-400'
                                : balance > 0
                                ? 'text-emerald-600'
                                : balance < 0
                                ? 'text-red-600'
                                : ''
                            }`}
                          >
                            {formatNumber(balance)}
                          </td>
                        ))}
                        <td
                          className={`border border-gray-200 p-2 sm:p-3 text-center font-mono font-bold text-xs sm:text-sm ${
                            account.difference > 0
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {formatNumber(account.difference)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-100 font-bold">
                      <td
                        colSpan={2}
                        className="border border-gray-200 p-2 sm:p-3 text-right text-sm sm:text-base"
                      >
                        الإجمالي
                      </td>
                      {comparisonData.totals.map((total, idx) => (
                        <td
                          key={idx}
                          className={`border border-gray-200 p-2 sm:p-3 text-center font-mono text-sm sm:text-base ${
                            total > 0
                              ? 'text-emerald-700'
                              : total < 0
                              ? 'text-red-700'
                              : ''
                          }`}
                        >
                          {formatNumber(total)}
                        </td>
                      ))}
                      <td className="border border-gray-200 p-2 sm:p-3 text-center font-mono text-sm sm:text-base text-amber-700">
                        {formatNumber(comparisonData.totalDifference)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-5 p-3 sm:p-4 bg-gray-50 rounded-xl">
              <p className="font-semibold text-sm mb-2">دليل الألوان:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-100 border border-emerald-300 rounded"></div>
                  <span>رصيد مدين (موجب)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                  <span>رصيد دائن (سالب)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
                  <span>غير موجود</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
